import { useState, useRef, memo, useMemo } from 'react';

import useUserStore from '@lcsc-ui/store/user';
import { buriedPoint } from '@lcsc-ui/utils/buriedPoint';
import Utils from '@lcsc-ui/utils/Utils';
import { Checkbox } from 'antd';

import CorrectDialog from '../../../correctDialog';

import Image from '@/components/Image';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

export default function ProductParams({ data, productPdfDescData }) {
  const projectConfigVO = useUserStore((state) => state.projectConfigVO);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRowKeysLeft, setSelectedRowKeysLeft] = useState([data?.currentCatalog?.categoryCode]);
  const [selectedRowKeysRight, setSelectedRowKeysRight] = useState([]);
  const similarProductBtnRef = useRef(null);

  function handleCorrectClose() {
    setIsModalOpen(false);
  }

  const querySimilarProductsCount = () => {
    const param = {
      catalogNodeId: data?.currentCatalog?.catalogId,
      queryParameterValue: '',
    };
    // 商品属于未分类
    if (!param.catalogNodeId) {
      return;
    }
    buriedPoint({
      pf: 'pc',
      ty: 'click',
      cp: 'product_detail',
      op: 'see_similar_products',
      data: data?.productRecord.productCode,
    });

    const paramList = data?.paramList;
    const rowKey = [...selectedRowKeysLeft, ...selectedRowKeysRight];
    const queryParameterList = paramList.filter((v) => rowKey.includes(v.parameterCode));
    queryParameterList?.forEach((val) => {
      param.queryParameterValue += val.parameterValue + '@' + val.parameterCode + '$';
    });

    param.queryParameterValue = encodeURIComponent(param.queryParameterValue);

    const baseUrl = `${projectConfigVO.lcscListUrl}/catalog/${param.catalogNodeId}.html?queryParameterValue=${encodeURIComponent(param.queryParameterValue) + (window.isMro ? '&mro=1' : '')}`;
    const finalUrl = Utils.getBtnSpmQueryUrl(baseUrl, similarProductBtnRef.current);
    window.open(finalUrl);
  };

  const TablePage = memo(() => {
    const paramObj = data?.currentCatalog;

    const dataSource = useMemo(() => {
      return data?.paramList && data?.paramList.length > 0
        ? [
            {
              parameterName: '商品目录',
              parameterValue: paramObj?.catalogName,
              parameterCode: paramObj?.categoryCode,
            },
          ].concat(data.paramList)
        : [];
    }, [paramObj]);

    const dataSourceLeft = useMemo(() => {
      return dataSource.slice(0, Math.ceil(dataSource.length / 2)) || [];
    }, [dataSource]);

    const dataSourceRight = useMemo(() => {
      return dataSource.slice(Math.ceil(dataSource.length / 2), dataSource.length) || [];
    }, [dataSource]);

    // 处理单个复选框选择
    const handleSelect = (type, id) => {
      const setSelectedRowKeys = type === 'left' ? setSelectedRowKeysLeft : setSelectedRowKeysRight;
      setSelectedRowKeys((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
    };

    // 处理全选
    const handleSelectAll = (type) => {
      const setSelectedRowKeys = type === 'left' ? setSelectedRowKeysLeft : setSelectedRowKeysRight;
      const selectedRowKeys = type === 'left' ? selectedRowKeysLeft : selectedRowKeysRight;
      const dataSource = type === 'left' ? dataSourceLeft : dataSourceRight;

      if (selectedRowKeys.length === dataSource.length) {
        type === 'left' ? setSelectedRowKeys([data?.currentCatalog?.categoryCode]) : setSelectedRowKeys([]);
      } else {
        setSelectedRowKeys(dataSource.map((item) => item.parameterCode));
      }
    };

    // 半选状态
    const indeterminateLeft = useMemo(() => {
      return selectedRowKeysLeft.length > 1 && selectedRowKeysLeft.length < dataSourceLeft.length;
    }, [dataSourceLeft]);

    const indeterminateRight = useMemo(() => {
      return !!selectedRowKeysRight.length && selectedRowKeysRight.length < dataSourceRight.length;
    }, [dataSourceRight]);

    return (
      <div className="flex justify-between">
        <Table wrapClassName="rounded-t-[6px]">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px] text-center">
                <Checkbox
                  indeterminate={indeterminateLeft}
                  checked={selectedRowKeysLeft.length === dataSourceLeft.length}
                  onChange={() => handleSelectAll('left')}
                />
              </TableHead>
              <TableHead className="w-[130px]">属性</TableHead>
              <TableHead>参数值</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {dataSourceLeft.map((item) => (
              <TableRow key={item.parameterCode}>
                <TableCell className="text-center">
                  <Checkbox
                    disabled={item.parameterName === '商品目录'}
                    checked={selectedRowKeysLeft.includes(item.parameterCode)}
                    onChange={() => handleSelect('left', item.parameterCode)}
                  />
                </TableCell>
                <TableCell>{item.parameterName}</TableCell>
                <TableCell>{item.parameterValue}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {dataSourceRight.length > 0 && (
          <Table wrapClassName="rounded-t-[6px] ml-6">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px] text-center">
                  <Checkbox
                    indeterminate={indeterminateRight}
                    checked={selectedRowKeysRight.length === dataSourceRight.length}
                    onChange={() => handleSelectAll('right')}
                  />
                </TableHead>
                <TableHead className="w-[130px]">属性</TableHead>
                <TableHead>参数值</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dataSourceRight.map((item) => (
                <TableRow key={item.parameterCode}>
                  <TableCell className="text-center">
                    <Checkbox
                      checked={selectedRowKeysRight.includes(item.parameterCode)}
                      onChange={() => handleSelect('right', item.parameterCode)}
                    />
                  </TableCell>
                  <TableCell>{item.parameterName}</TableCell>
                  <TableCell>{item.parameterValue}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    );
  });
  TablePage.displayName = 'TablePage';

  const OverseaTablePage = memo(() => {
    const dataSource = [];
    Object.keys(data?.paramMap).forEach((k) => {
      const item = {
        parameterName: k,
        parameterValue: data?.paramMap[k],
        parameterCode: data?.paramMap[k],
      };
      dataSource.push(item);
    });
    const dataSourceLeft = dataSource.slice(0, Math.ceil(dataSource.length / 2)) || [];
    const dataSourceRight = dataSource.slice(Math.ceil(dataSource.length / 2), dataSource.length) || [];
    return (
      <div className="flex justify-between">
        <Table className="mb-2">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[130px]">属性</TableHead>
              <TableHead>参数值</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {dataSourceLeft.map((item) => (
              <TableRow key={item.parameterCode}>
                <TableCell>{item.parameterName}</TableCell>
                <TableCell>{item.parameterValue}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {dataSourceRight.length > 0 && (
          <Table wrapClassName="ml-6" className="mb-2">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[130px]">属性</TableHead>
                <TableHead>参数值</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dataSourceRight.map((item) => (
                <TableRow key={item.parameterCode}>
                  <TableCell>{item.parameterName}</TableCell>
                  <TableCell>{item.parameterValue}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    );
  });
  OverseaTablePage.displayName = 'OverseaTablePage';

  const nullParams = (
    <section
      id="productParamsTabItem"
      className="mt-[72px] w-[810px] pt-[20px] pb-[30px] pl-[20px] pr-[20px] rounded-b-[20px] bg-white"
      data-spm="cs"
    >
      <div className="flex items-center justify-between mb-[19px] align-baseline">
        <h2 className="font-bold text-[18px] text-[#0B1832]">商品参数</h2>
        <div className="text-[14px] flex items-center">
          <button
            id="item-error-correction"
            className="text-[#0093E6] bg-[white] rounded-[4px] border-[1px] border-solid border-[#0093E6] mr-[20px] w-[76px] h-[28px] hover:opacity-[0.6]"
            onClick={() => setIsModalOpen(true)}
          >
            资料纠错
          </button>
          {data?.paramList?.length > 0 && (
            <button
              id="item-similar-product"
              className="text-[#FFFFFF] rounded-[4px] bg-[#0093E6] w-[100px] h-[28px] hover:opacity-[0.6]"
              onClick={querySimilarProductsCount}
            >
              查看类似商品
            </button>
          )}
        </div>
      </div>
      <div className="w-[112px] m-auto">
        <Image
          width={112}
          height={84}
          src={'https://static.szlcsc.com/ecp/assets/newWeb/item/null.svg'}
          alt={'暂无内容图标'}
        />
        <p className="text-[#ABB4BE] text-[14px] ml-[10px] mt-[16px]">参数完善中</p>
      </div>
      {isModalOpen && <CorrectDialog {...data?.productRecord} onClose={handleCorrectClose} />}
    </section>
  );

  const localParams =
    data?.paramList?.length > 0 ||
    (productPdfDescData?.productIntroVO?.productIntro &&
      (productPdfDescData?.isMroProduct || !productPdfDescData?.productPDFDescVO)) ? (
      <section
        id="productParamsTabItem"
        className="mt-[72px] w-[810px] pt-[20px] pb-[30px] pl-[20px] pr-[20px] rounded-b-[20px] bg-white"
        data-spm="cs"
      >
        <div className="flex items-center justify-between mb-[19px] align-baseline">
          <h2 className="font-bold text-[18px] text-[#0B1832]">商品参数</h2>
          <div className="text-[14px] flex items-center">
            <button
              id="item-error-correction"
              className="text-[#0093E6] bg-[white] rounded-[4px] border-[1px] border-solid border-[#0093E6] mr-[20px] w-[76px] h-[28px] hover:opacity-[0.6]"
              onClick={() => setIsModalOpen(true)}
            >
              资料纠错
            </button>
            {data?.paramList?.length > 0 && (
              <button
                id="item-similar-product"
                ref={similarProductBtnRef}
                className="text-[#FFFFFF] rounded-[4px] bg-[#0093E6] w-[100px] h-[28px] hover:opacity-[0.6]"
                onClick={querySimilarProductsCount}
                data-spm="ls"
              >
                查看类似商品
              </button>
            )}
          </div>
        </div>
        {data?.paramList && data?.paramList?.length > 0 && <TablePage />}
        {productPdfDescData?.productIntroVO?.productIntro &&
        (productPdfDescData?.isMroProduct || !productPdfDescData?.productPDFDescVO) ? (
          <div
            className="prose !max-w-none text-[14px]"
            id="productIntro"
            dangerouslySetInnerHTML={{ __html: productPdfDescData?.productIntroVO?.productIntro }}
          ></div>
        ) : (
          <></>
        )}
        {isModalOpen && <CorrectDialog {...data?.productRecord} onClose={handleCorrectClose} />}
      </section>
    ) : (
      nullParams
    );

  const overseaParams = data?.paramMap && (
    <section
      id="productParamsTabItem"
      className="mt-[20px] w-[810px] pt-[20px] pb-[30px] pl-[30px] pr-[20px] rounded-[20px] bg-white"
    >
      <h2 className="font-bold text-[18px] text-[#0B1832] mb-[20px]">商品参数</h2>
      <OverseaTablePage />
    </section>
  );

  return data?.isOversea ? overseaParams : localParams;
}
